import { Loader2 } from "lucide-react";
import { Alert, AlertDescription, Button, Checkbox, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label } from "randmarcomps";
import { inject, observer } from "mobx-react";
import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { RootStore } from "../../stores/rootStore";
import { setUserSelectedOpportunity } from '../../utils/LocalStorageUtils';

interface IProps {
  store?: RootStore;
  disabled?: boolean;
  children: React.ReactNode;
}

const RenewOpportunityWrapper: React.FC<IProps> = ({ store, disabled, children }) => {
  const { applicationId } = useManufacturer();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [startDate, setStartDate] = useState<string | undefined>();
  const [endDate, setEndDate] = useState<string | undefined>();
  const [bidNumber, setBidNumber] = useState<string | undefined>();
  const [bidTitle, setBidTitle] = useState<string | undefined>();
  const [quoteOnly, setQuoteOnly] = useState(false);
  const [renewed, setRenewed] = useState(false);

  const { snackStore, opportunityStore } = store!;
  const { opportunity } = opportunityStore;

  const renewOpportunity = async () => {
    setLoading(true);

    opportunityStore.renewOpportunity(applicationId, opportunity!.bidNumber || "", bidNumber || "", bidTitle || "", startDate || "", endDate || "", quoteOnly)
      .then((success) => {
        if (success) {
          snackStore.showSuccess(`Opportunity renewed.`);
          setUserSelectedOpportunity(bidNumber || "");
          setRenewed(true);
        } else {
          snackStore.showError(`Opportunity could not be renewed.`);
        }

        setLoading(false);
        setOpen(false);

        opportunityStore.getOpportunities(applicationId);
      });
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleClose = () => {
    setRenewed(false);
  };

  const handleRedirect = () => {
    setRedirect(true);
  };

  const handleEditInformationClicked = () => {
    if (opportunity) {
      setBidNumber(opportunity.bidNumber);
      setBidTitle(opportunity.opportunity);
      setStartDate(opportunity.startDate);
      setEndDate(opportunity.endDate);
    }
    setOpen(true);
  };

  if (redirect) return <Navigate to={Path.opportunities(applicationId)} />;

  return (
    <>
      <div onClick={handleEditInformationClicked}>
        {children}
      </div>
      <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen && !loading) { handleCancel(); } }}>
        <DialogContent>
          <div hidden={loading}>
            <DialogHeader>
              <DialogTitle>Renew Opportunity</DialogTitle>
            </DialogHeader>
            <Alert variant="default" className="mb-4">
              <AlertDescription>
                This will create a new opportunity with the same resellers and rebates on the same part numbers.
              </AlertDescription>
            </Alert>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <div>
                <Label htmlFor="bidNumber">Opportunity Number</Label>
                <Input
                  id="bidNumber"
                  type="text"
                  value={bidNumber || ''}
                  onChange={e => setBidNumber(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="bidTitle">Opportunity Title</Label>
                <Input
                  id="bidTitle"
                  type="text"
                  value={bidTitle || ''}
                  onChange={e => setBidTitle(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate || ''}
                  onChange={e => setStartDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate || ''}
                  onChange={e => setEndDate(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2 mt-4 ml-1">
              <Checkbox
                id="quoteOnly"
                checked={quoteOnly}
                onCheckedChange={(checked) => setQuoteOnly(Boolean(checked))}
              />
              <Label htmlFor="quoteOnly" className="cursor-pointer">Quote Only</Label>
            </div>
            <DialogFooter className="mt-4 flex justify-between">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                disabled={!startDate || !endDate || !bidTitle || !bidNumber || startDate > endDate || disabled}
                onClick={() => renewOpportunity()}>
                Confirm
              </Button>
            </DialogFooter>
          </div>
          <div hidden={!loading} className="p-8 text-center">
            <DialogHeader>
              <DialogTitle>
                Renew Opportunity
              </DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center justify-center gap-4 py-4">
              <Loader2 className="h-8 w-8 animate-spin" />
              <p>Please wait...</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={renewed} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renew Opportunity</DialogTitle>
          </DialogHeader>
          <div className="py-4">{opportunity?.opportunity} was succesfully renewed</div>
          <DialogFooter className="flex justify-between">
            <Button variant="outline" onClick={handleClose}>Close</Button>
            {renewed && <Button onClick={handleRedirect}>View Opportunities</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default inject('store')(observer(RenewOpportunityWrapper));
